/**
 * CrmOpportunityDetailModal — view an opportunity (pursuit-stage Project): stage management, fee +
 * weighted value, proposal status, and an interaction timeline + logging. Proposals authoring lands
 * in the proposals step; a placeholder marks where they'll attach.
 */
import { useEffect, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import CrmLogInteractionModal from './CrmLogInteractionModal.jsx'
import {
  updateContractStage, MANUAL_STAGES, SYSTEM_STAGES, STAGE_PROB, LOST_STAGES,
  COMPLEXITY_OPTS, PORTFOLIO_OPTS, CLIENT_STYLE_OPTS, moveToLostStage, getOpportunityDetail,
  syncOpportunityContactsToClient,
} from '../../services/opportunities.js'
import { updateProjectDetails } from '../../services/projects.js'
import { getOrganizationDetail, updateOrganization } from '../../services/contacts.js'
import { getInteractionsForOpportunity } from '../../services/interactions.js'
import { getLink, getOrCreateLink, setStatus, listJourneySubscribers } from '../../services/journey.js'
import { getProposalsForOpportunity, getCountersignStatus } from '../../services/proposals.js'
import { getLeadDetail } from '../../services/leads.js'
import SlackThreadPanel from '../SlackThreadPanel.jsx'
import CrmProposalDetailModal from './CrmProposalDetailModal.jsx'
import { syncOpen } from './crmSync.js'
import CrmProposalEditor from './CrmProposalEditor.jsx'
import CrmProposalTypePicker from './CrmProposalTypePicker.jsx'
import { getAsrsForProject } from '../../services/asrs.js'
import CrmAsrEditor from './CrmAsrEditor.jsx'
import { getReferencesForOpportunity } from '../../services/references.js'
import CrmReferenceDetailModal from './CrmReferenceDetailModal.jsx'
import CrmDrawingsSection from './CrmDrawingsSection.jsx'
import CrmFitSlider from './CrmFitSlider.jsx'
import CrmLossReasonModal from './CrmLossReasonModal.jsx'
import OrganizationPicker from './OrganizationPicker.jsx'
import { STATE_OPTIONS } from './CrmNewLeadWizard.jsx'
import { splitAddress, cityProblem } from '../../lib/addressSplit.js'
import { opportunityNextStep } from './crmOpportunityNextStep.js'
import { URGENCY } from './crmLeadNextStep.js'
import { alertDialog } from '../Dialog.jsx'

function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '—' }
function fmtDate(v) { if (!v) return ''; try { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return String(v) } }
const TYPE_ICON = { 'Phone Call': '📞', 'Video Conference': '🎥', 'Email': '✉️', 'Text Message': '💬', 'In Person': '🤝', 'Action Taken': '⚡' }

export default function CrmOpportunityDetailModal({ opp, onClose, onChanged }) {
  const [stage, setStage] = useState(opp.stage)
  const [busy, setBusy] = useState(false)
  const [interactions, setInteractions] = useState(null)
  const [proposals, setProposals] = useState(null)
  const [openProposal, setOpenProposal] = useState(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [newType, setNewType] = useState(null)
  const [asrs, setAsrs] = useState(null)
  const [openAsr, setOpenAsr] = useState(undefined)   // undefined closed; null = new; obj = edit
  const [references, setReferences] = useState(null)
  const [openReference, setOpenReference] = useState(undefined)   // undefined closed; null = new; obj = edit
  // Derived from the proposals list's own Status (not the Airtable 'Executed BS/DC Proposal' link
  // fields on the Project — those can be stale/premature; see docs/CRM.md and the countersign-time
  // write in api/proposal-pdf.js#freezeCountersignedProposal, which is now the only place that
  // link is ever actually set).
  const execDC = Array.isArray(proposals) && proposals.some(pr => pr.type === 'Design Consultation' && pr.status === 'Executed')
  const execBS = Array.isArray(proposals) && proposals.some(pr => pr.type === 'Basic Services' && pr.status === 'Executed')
  const [logOpen, setLogOpen] = useState(false)
  const [logPrefill, setLogPrefill] = useState(null)
  const [journey, setJourney] = useState(null)   // { id, link } once started
  const [jBusy, setJBusy] = useState(false)
  const [subs, setSubs] = useState(null)   // Client Hub email subscribers; null = not loaded
  const [countersignStatus, setCountersignStatus] = useState(null)
  const [lossReasonTarget, setLossReasonTarget] = useState(null)   // stage awaiting a reason, or null
  const loadedRef = useRef(false)

  // The Slack discussion lives on the originating LEAD, not this Project — entityType:'lead'
  // resolves to the fixed #aj_biz-dev channel server-side (api/slack.js), so the conversation
  // that started pre-conversion continues in the same thread instead of forking a new one.
  // opp.raw carries only Project fields, so the lead's 'Slack Thread Timestamp' needs its own
  // fetch (below) rather than reading off opp directly.
  const [showSlack, setShowSlack] = useState(false)
  const [localThreadTs, setLocalThreadTs] = useState('')

  // Editable overview — name + address. Optimistic like the fit-assessment ratings above: update
  // local state immediately, revert + alert on a failed PATCH.
  const [name, setName] = useState(opp.name)
  const [street, setStreet] = useState(opp.raw?.['Project Street Address'] || '')
  const [city, setCity] = useState(opp.raw?.['Project City'] || '')
  const [stateVal, setStateVal] = useState(opp.raw?.['Project State'] || '')
  const [zip, setZip] = useState(opp.raw?.['Project Zip'] || '')

  // Fit assessment — Complexity + Portfolio value live on this Project record; Client style lives
  // on the linked Client Organization instead (a schema quirk — "how easy to work with" is a
  // property of the client relationship, not any one project — see services/opportunities.js).
  // State, not a derived const — the whole point of surfacing this field is to let the linked
  // organization actually CHANGE (e.g. a design-build GC swapped out for the property owner once
  // the delivery method changes), not just display it.
  const [clientOrgId, setClientOrgId] = useState(Array.isArray(opp.raw?.['Client']) ? opp.raw['Client'][0] : null)
  const [clientOrgName, setClientOrgName] = useState('')
  const [ratings, setRatings] = useState({
    complexity: opp.raw?.['Complexity Rating'] || '',
    portfolio: opp.raw?.['Portfolio Rating'] || '',
    client: '',
  })

  // 2026-07: access is per-recipient now (database/134_journey_recipients.sql) — there is no
  // longer one shared link to copy and hand to the prospect directly (that link, when it existed,
  // also had no PIN gate for a brand-new journey — client_journeys.pin only gets set via the
  // legacy-only regenerateToken path, so "copy link" here was quietly handing out completely
  // unprotected access). Starting a journey now opens the SAME "Manage journey access" modal
  // JourneyAuthorSection.jsx uses, so the prospect gets added as a proper recipient with their own
  // link + PIN, whether this is the first time or a return visit to an already-started journey.
  function openManageAccess(journeyId) {
    const id = journeyId || (journey && journey.id)
    if (!id) return
    window.dispatchEvent(new CustomEvent('open-send-journey-access-modal', { detail: { journeyId: id, projectId: opp.id } }))
  }
  async function startJourney() {
    if (jBusy) return
    setJBusy(true)
    try {
      const { journey: j, link } = await getOrCreateLink(opp.id, opp.name)
      if (j && j.id && j.status !== 'active') { try { await setStatus(j.id, 'active') } catch (_) {} }
      setJourney({ id: j && j.id, link })
      openManageAccess(j && j.id)
    } finally { setJBusy(false) }
  }

  // Team-only — opens JourneyPreviewOverlay (Clerk-gated, never the real client link). Passes
  // along whichever proposal is currently being drafted (not yet posted, so it has no journey
  // entry of its own yet) so the overlay's own "Show draft proposal" toggle can insert it as a
  // synthetic preview card — lets the team check placement/copy against the rest of the journey
  // before actually posting, without creating any real journey_entries row.
  function previewJourneyForOpportunity() {
    if (!journey || !journey.id) return
    const draft = Array.isArray(proposals) ? proposals.find(p => !p.posted && p.type !== 'ASR Proposal') : null
    window.dispatchEvent(new CustomEvent('open-journey-preview', {
      detail: {
        journeyId: journey.id,
        draftProposal: draft ? {
          id: draft.id, type: draft.type, name: draft.name,
          contractFee: draft.contractFee, retainer: draft.retainer, tiered: draft.tiered, packages: draft.packages,
        } : null,
      },
    }))
  }

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    getInteractionsForOpportunity(opp.id).then(setInteractions).catch(() => setInteractions([]))
    getProposalsForOpportunity(opp.id).then(setProposals).catch(() => setProposals([]))
    getAsrsForProject(opp.id).then(setAsrs).catch(() => setAsrs([]))
    getReferencesForOpportunity(opp.id).then(setReferences).catch(() => setReferences([]))
    // Show the REAL journey state on open — without this, the box always shows the first-time
    // "Start" action even when a journey has been live for weeks, which reads as "did I already
    // do this?" and risks someone treating a routine re-open as an undo.
    getLink(opp.id).then(({ journey: j, link }) => { if (j) setJourney({ id: j.id, link }) }).catch(() => {})
    if (opp.leadId) {
      getLeadDetail(opp.leadId).then(lead => { if (lead) setLocalThreadTs(lead.slackThreadTs || '') }).catch(() => {})
    }
    if (clientOrgId) {
      getOrganizationDetail(clientOrgId)
        .then(org => {
          setRatings(r => ({ ...r, client: (org && org.fields && org.fields['Client Rating']) || '' }))
          setClientOrgName((org && org.fields && org.fields['Organization Name']) || '')
        })
        .catch(() => {})
    }
  }, [opp]) // eslint-disable-line react-hooks/exhaustive-deps
  function reloadAsrs() { getAsrsForProject(opp.id).then(list => { setAsrs(list); setOpenAsr(syncOpen(list)) }).catch(() => {}) }

  // Subscribers can only exist once a journey has been started — fetch once getLink() above
  // resolves one (or startJourney() creates one).
  useEffect(() => {
    if (!journey || !journey.id) return
    listJourneySubscribers(journey.id).then(({ subscribers }) => setSubs(subscribers || [])).catch(() => setSubs([]))
  }, [journey && journey.id])

  // The Next-Step engine needs the countersign status of whichever proposal is currently active
  // (Posted = out for client signature, Pending = client signed, awaiting our countersignature) —
  // reuses the same endpoint CrmProposalDetailModal already calls, no new plumbing.
  //
  // Fetch for 'Posted' too, not just 'Pending' (2026-08 fix): this used to gate on status ===
  // 'Pending' only — the Airtable Status a proposal's own record only reaches once EVERY required
  // signer has signed (see journey-respond.js's multi-signer gate). A joint (two-signer) proposal
  // sitting at 1-of-2 signed stays 'Posted' the whole time it's partially signed, so the fetch never
  // ran and cs.signers (the per-signer breakdown) was never available here — the Opportunity view
  // had zero visibility into partial signature progress even though CrmProposalDetailModal, which
  // fetches unconditionally, showed it correctly. getCountersignStatus is cheap and safe to call for
  // any posted proposal; it simply returns no signers for a fresh one nobody has signed yet.
  const activeProposal = Array.isArray(proposals) ? proposals.find(p => p.status === 'Posted' || p.status === 'Pending') : null
  useEffect(() => {
    if (!activeProposal) { setCountersignStatus(null); return }
    let cancelled = false
    getCountersignStatus(activeProposal.id).then(s => { if (!cancelled) setCountersignStatus(s) }).catch(() => {})
    return () => { cancelled = true }
  }, [activeProposal && activeProposal.id, activeProposal && activeProposal.status])

  // Opening a deal puts its contacts on its client organization (services/opportunities.js) —
  // the conversion automation links the contact to the opportunity but not always to the org.
  useEffect(() => {
    if (opp && opp.id) syncOpportunityContactsToClient(opp.id).catch(() => {})
  }, [opp && opp.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function saveOverviewField(field, value, revert) {
    try {
      await updateProjectDetails(opp.id, { [field]: value })
      if (onChanged) onChanged()
      // Client Hub's project name is a cached snapshot, not live-computed — re-sync it
      // immediately on rename so an already-started journey doesn't keep showing a stale name.
      if (field === 'Project Name' && journey && journey.id) getOrCreateLink(opp.id, value).catch(() => {})
    }
    catch (e) { revert(); await alertDialog({ title: 'Save failed', message: (e && e.message) || `Could not save ${field}.` }) }
  }
  function saveName(v) { const prev = name; setName(v); saveOverviewField('Project Name', v, () => setName(prev)) }
  // A whole address typed or pasted into Street fills all four (lib/addressSplit.js).
  async function saveStreet(v) {
    const parts = splitAddress(v)
    if (!parts) { const prev = street; setStreet(v); saveOverviewField('Project Street Address', v, () => setStreet(prev)); return }
    const prev = { street, city, stateVal, zip }
    setStreet(parts.street); setCity(parts.city); setStateVal(parts.state); if (parts.zip) setZip(parts.zip)
    try {
      await updateProjectDetails(opp.id, {
        'Project Street Address': parts.street, 'Project City': parts.city, 'Project State': parts.state,
        ...(parts.zip ? { 'Project Zip': parts.zip } : {}),
      })
      if (onChanged) onChanged()
    } catch (e) {
      setStreet(prev.street); setCity(prev.city); setStateVal(prev.stateVal); setZip(prev.zip)
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the address.' })
    }
  }
  async function saveCity(v) {
    const bad = cityProblem(v)
    if (bad) { await alertDialog({ title: 'City not saved', message: bad }); return }
    const prev = city; setCity(v); saveOverviewField('Project City', v, () => setCity(prev))
  }
  function saveState(v) { const prev = stateVal; setStateVal(v); saveOverviewField('Project State', v, () => setStateVal(prev)) }
  function saveZip(v) { const prev = zip; setZip(v); saveOverviewField('Project Zip', v, () => setZip(prev)) }

  async function setComplexity(val) {
    const prev = ratings.complexity
    setRatings(r => ({ ...r, complexity: val }))
    try { await updateProjectDetails(opp.id, { 'Complexity Rating': val }); if (onChanged) onChanged() }
    catch (e) { setRatings(r => ({ ...r, complexity: prev })); await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save Complexity.' }) }
  }
  async function setPortfolio(val) {
    const prev = ratings.portfolio
    setRatings(r => ({ ...r, portfolio: val }))
    try { await updateProjectDetails(opp.id, { 'Portfolio Rating': val }); if (onChanged) onChanged() }
    catch (e) { setRatings(r => ({ ...r, portfolio: prev })); await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save Portfolio value.' }) }
  }
  // Reassign which Organization this opportunity is directed at — e.g. a design-build GC swapped
  // out for the property owner once the delivery method changes to traditional. Optimistic, like
  // the other Overview fields; on failure, reverts both id and name together.
  async function changeClientOrg(newId, newName) {
    const prevId = clientOrgId, prevName = clientOrgName, prevRating = ratings.client
    setClientOrgId(newId || null); setClientOrgName(newName || '')
    try {
      await updateProjectDetails(opp.id, { 'Client': newId ? [newId] : [] })
      if (newId) syncOpportunityContactsToClient(opp.id).catch(() => {})
      if (onChanged) onChanged()
      // Client style lives on the ORGANIZATION, not the Project — re-fetch it for whichever org
      // is now linked so the slider doesn't keep showing the previous org's rating.
      if (newId) {
        getOrganizationDetail(newId)
          .then(org => setRatings(r => ({ ...r, client: (org && org.fields && org.fields['Client Rating']) || '' })))
          .catch(() => {})
      } else {
        setRatings(r => ({ ...r, client: '' }))
      }
    } catch (e) {
      setClientOrgId(prevId); setClientOrgName(prevName); setRatings(r => ({ ...r, client: prevRating }))
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not change the linked organization.' })
    }
  }
  async function setClientStyle(val) {
    if (!clientOrgId) { await alertDialog({ title: 'No client organization', message: 'This opportunity has no linked Client organization to rate.' }); return }
    const prev = ratings.client
    setRatings(r => ({ ...r, client: val }))
    try { await updateOrganization(clientOrgId, { 'Client Rating': val }); if (onChanged) onChanged() }
    catch (e) { setRatings(r => ({ ...r, client: prev })); await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save Client style.' }) }
  }

  async function changeStage(next) {
    const prev = stage
    setStage(next); setBusy(true)
    try { await updateContractStage(opp.id, next, prev); if (onChanged) onChanged() }
    finally { setBusy(false) }
  }

  // Lost/No-Go stages require a reason first — routes through CrmLossReasonModal instead of
  // patching immediately. Everything else in MANUAL_STAGES is a plain judgment-call move.
  function handleStageChange(next) {
    if (LOST_STAGES.indexOf(next) !== -1) { setLossReasonTarget(next); return }
    changeStage(next)
  }

  async function confirmLoss(reason, notes) {
    await moveToLostStage(opp.id, lossReasonTarget, reason, notes, stage)
    setStage(lossReasonTarget)
    setLossReasonTarget(null)
    if (onChanged) onChanged()
  }

  const prob = STAGE_PROB[stage]
  const weighted = LOST_STAGES.indexOf(stage) !== -1 ? 0 : (opp.fee * (prob != null ? prob : 0.1))
  const nextStep = opportunityNextStep({ ...opp, stage }, interactions || [], countersignStatus)

  return (
    <>
      <CrmModal title={name} onClose={onClose} width={620}>
        <div style={S.metaRow}>
          {SYSTEM_STAGES.indexOf(stage) !== -1 ? (
            <span style={S.systemStageBadge} title="Set automatically by the app when the matching event happens (proposal posted, client signed, countersigned) — not manually editable.">
              {stage} <span style={{ opacity: 0.55 }}>ⓘ</span>
            </span>
          ) : (
            <select style={{ ...F.input, width: 'auto', fontWeight: 600 }} value={stage} onChange={e => handleStageChange(e.target.value)} disabled={busy}>
              {MANUAL_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {prob != null && <span style={S.prob}>{Math.round(prob * 100)}% close</span>}
          <div style={{ flex: 1 }} />
          {opp.leadId && (
            <button
              onClick={() => setShowSlack(true)}
              title={localThreadTs ? 'Open team discussion (Slack)' : 'Start a team discussion (Slack)'}
              style={{
                background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 5,
                padding: '6px 8px', cursor: 'pointer', color: 'var(--aj-text-mid)',
                display: 'flex', alignItems: 'center', flexShrink: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          )}
        </div>

        {interactions !== null && (
          <OpportunityNextStepCard step={nextStep} onLog={() => { setLogPrefill(nextStep.prefill || null); setLogOpen(true) }} />
        )}

        <div style={S.fieldGrid}>
          <Field label="Estimated fee" value={money(opp.fee)} mono />
          <Field label="Weighted" value={money(weighted)} mono />
          <Field label="Project type" value={opp.projectType} />
          <Field label="Est. construction value" value={opp.estValue ? money(opp.estValue) : ''} mono />
          <Field label="Proposal date" value={fmtDate(opp.proposalDate)} mono />
        </div>

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>Overview</span>
        </div>
        <div style={{ marginBottom: 10 }}>
          <TextField label="Project name" value={name} onCommit={saveName} />
        </div>
        {/* Who the pursuit is directed at — the Projects.Client Organization link. Shown AND
            reassignable (the picker's "change" swaps the linked record, never renames the org):
            the client on a deal can genuinely change, e.g. a design-build GC replaced by the
            property owner when the delivery method shifts to traditional. */}
        <div style={{ marginBottom: 10 }}>
          <div style={F.label}>Client / prospect organization</div>
          <OrganizationPicker value={clientOrgId || ''} name={clientOrgName} onChange={changeClientOrg} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <TextField label="Street address" value={street} onCommit={saveStreet} />
        </div>
        <div style={S.addrGrid}>
          <TextField label="City" value={city} onCommit={saveCity} />
          <SelectField label="State" value={stateVal} onCommit={saveState} options={STATE_OPTIONS} />
          <TextField label="Zip" value={zip} onCommit={saveZip} />
        </div>

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>Fit assessment</span>
        </div>
        <CrmFitSlider label="Complexity" options={COMPLEXITY_OPTS} value={ratings.complexity} onChange={setComplexity} />
        <CrmFitSlider label="Portfolio value" options={PORTFOLIO_OPTS} value={ratings.portfolio} onChange={setPortfolio} />
        <CrmFitSlider label="Client style" options={CLIENT_STYLE_OPTS} value={ratings.client} onChange={setClientStyle} disabled={!clientOrgId} />

        <div style={S.journeyBox}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={S.journeyLabel}>Client Hub</span>
            <div style={{ flex: 1 }} />
            {!journey
              ? <button style={F.btnGhost} onClick={startJourney} disabled={jBusy}>{jBusy ? 'Starting…' : 'Start & manage access'}</button>
              : <>
                  <button style={F.btnGhost} onClick={previewJourneyForOpportunity}>Preview hub</button>
                  <button style={F.btnGhost} onClick={() => openManageAccess()}>Manage access</button>
                </>}
          </div>
          <div style={S.journeyHint}>Goes live immediately with a suspense screen. Add the prospect in Manage access — each recipient gets their own private link and access code by email; the proposal appears here when ready, to review and sign.</div>
        </div>

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>Subscribers</span>
          {subs && <span style={{ ...S.propStatus, marginLeft: 8 }}>{subs.filter(s => s.status === 'active').length} active</span>}
        </div>
        {!journey ? <div style={S.muted}>Start the Client Hub above to enable email subscriptions.</div>
          : subs === null ? <div style={S.muted}>Loading…</div>
          : subs.length === 0 ? <div style={S.muted}>No one has subscribed to email updates yet.</div>
          : subs.map(s => (
            <div key={s.id} style={S.subRow}>
              <span style={{ ...S.subEmail, ...(s.status !== 'active' ? S.subEmailInactive : null) }}>{s.email}</span>
              {s.status !== 'active'
                ? <span style={S.subBadge}>Unsubscribed</span>
                : <span style={S.subDate}>{fmtDate(s.created_at)}</span>}
            </div>
          ))}

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>Proposals</span>
          <div style={{ flex: 1 }} />
          <button style={{ ...F.btnGhost, padding: '4px 10px' }} onClick={() => setPickerOpen(true)}>+ New proposal</button>
        </div>
        {proposals === null ? <div style={S.muted}>Loading…</div>
          : proposals.length === 0 ? <div style={S.muted}>No proposals yet.</div>
          : proposals.map(pr => (
            <button key={pr.id} style={S.propRow} onClick={() => setOpenProposal(pr)}>
              <span style={S.propType}>{pr.type === 'Design Consultation' ? 'DC' : pr.type === 'ASR Proposal' ? 'ASR' : 'BS'}</span>
              <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }}>{pr.name || pr.clientName || 'Proposal'}</span>
              <span style={S.propStatus}>{pr.status}</span>
              <span style={S.propFee}>{pr.contractFee ? '$' + Math.round(pr.contractFee).toLocaleString() : (pr.retainer ? '$' + Math.round(pr.retainer).toLocaleString() : '')}</span>
            </button>
          ))}

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>Additional Services (ASRs)</span>
          <div style={{ flex: 1 }} />
          <button style={{ ...F.btnGhost, padding: '4px 10px' }} onClick={() => setOpenAsr(null)}>+ New ASR</button>
        </div>
        {asrs === null ? <div style={S.muted}>Loading…</div>
          : asrs.length === 0 ? <div style={S.muted}>No additional services requests.</div>
          : asrs.map(a => (
            <button key={a.id} style={S.propRow} onClick={() => setOpenAsr(a)}>
              <span style={S.propType}>#{a.number}</span>
              <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }}>{a.title || 'ASR'}</span>
              <span style={S.propStatus}>{a.status}</span>
              <span style={S.propFee}>{a.totalFee ? '$' + Math.round(a.totalFee).toLocaleString() : ''}</span>
            </button>
          ))}

        <div style={S.sectionRow}>
          <span style={S.sectionLbl}>References</span>
          <div style={{ flex: 1 }} />
          <button style={{ ...F.btnGhost, padding: '4px 10px' }} onClick={() => setOpenReference(null)}>+ New reference letter</button>
        </div>
        {references === null ? <div style={S.muted}>Loading…</div>
          : references.length === 0 ? <div style={S.muted}>No reference letters yet.</div>
          : references.map(ref => (
            <button key={ref.id} style={S.propRow} onClick={() => setOpenReference(ref)}>
              <span style={S.propType}>{ref.referenceIds.length}</span>
              <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text)' }}>{ref.title}</span>
              <span style={S.propStatus}>{ref.posted ? 'Live' : 'Draft'}</span>
            </button>
          ))}

        <CrmDrawingsSection opportunityId={opp.id} />

        <div style={S.histHeadRow}>
          <span style={S.histHead}>Interactions</span>
          <button style={{ ...F.btnGhost, padding: '3px 10px', fontSize: 11.5 }} onClick={() => { setLogPrefill(null); setLogOpen(true) }} disabled={busy}>+ Log interaction</button>
        </div>
        {interactions === null ? <div style={S.muted}>Loading…</div>
          : interactions.length === 0 ? <div style={S.muted}>No interactions logged yet.</div>
          : interactions.map(it => (
            <div key={it.id} style={S.histRow}>
              <div style={S.histIcon}>{TYPE_ICON[it.type] || '•'}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.histType}>{[it.type, it.opportunityType].filter(Boolean).join(' · ')}</div>
                {it.notes && <div style={S.histNotes}>{String(it.notes).replace(/[*_#>`]/g, '')}</div>}
                <div style={S.histMeta}>{fmtDate(it.date)}{it.teamMember ? ` · ${it.teamMember}` : ''}</div>
              </div>
            </div>
          ))}
      </CrmModal>

      {logOpen && (
        <CrmLogInteractionModal projectId={opp.id} prefill={logPrefill} onClose={() => { setLogOpen(false); setLogPrefill(null) }}
          onLogged={it => { if (it) { setInteractions(list => [it, ...(list || [])]); if (onChanged) onChanged() } }} />
      )}
      {lossReasonTarget && (
        <CrmLossReasonModal stage={lossReasonTarget} onClose={() => setLossReasonTarget(null)} onConfirm={confirmLoss} />
      )}
      {showSlack && opp.leadId && (
        <SlackThreadPanel
          entityId={opp.leadId}
          entityType="lead"
          entityName={name || 'Lead'}
          threadTs={localThreadTs}
          onClose={() => setShowSlack(false)}
          onThreadStarted={ts => setLocalThreadTs(ts)}
        />
      )}
      {openProposal && <CrmProposalDetailModal proposal={openProposal} onClose={() => setOpenProposal(null)} onChanged={() => {
        // syncOpen re-points the OPEN modal at its refreshed row, not just the list behind it —
        // without it this modal (and the editor it opens) keep rendering the snapshot captured
        // when the row was clicked, which is what made saved edits look reverted until a reload.
        getProposalsForOpportunity(opp.id).then(list => { setProposals(list); setOpenProposal(syncOpen(list)) }).catch(() => {})
        // Post/unpost/countersign all change Contract Stage server-side (markPosted's
        // advanceToProposalPosted, proposal-reset's revert, freezeCountersignedProposal's
        // advance to Executed) — without this, the stage badge/dropdown above stayed on
        // whatever it was when the modal first opened until a full page reload.
        getOpportunityDetail(opp.id).then(o => { if (o) setStage(o.stage) }).catch(() => {})
        if (onChanged) onChanged()
      }} />}
      {openAsr !== undefined && (
        <CrmAsrEditor asr={openAsr || undefined} opportunityId={opp.id} opportunityName={opp.name}
          onClose={() => setOpenAsr(undefined)} onSaved={() => { reloadAsrs(); if (onChanged) onChanged() }} />
      )}
      {openReference !== undefined && (
        <CrmReferenceDetailModal reference={openReference || undefined} opportunityId={opp.id} opportunityName={opp.name}
          onClose={() => setOpenReference(undefined)}
          onChanged={() => { getReferencesForOpportunity(opp.id).then(list => { setReferences(list); setOpenReference(syncOpen(list)) }).catch(() => {}); if (onChanged) onChanged() }} />
      )}
      {pickerOpen && (
        <CrmProposalTypePicker hasExecutedDC={execDC} hasExecutedBS={execBS}
          onClose={() => setPickerOpen(false)}
          onPick={t => { setPickerOpen(false); setNewType(t) }} />
      )}
      {newType && (
        <CrmProposalEditor opportunityId={opp.id} opportunityName={opp.name} initialType={newType}
          onClose={() => setNewType(null)}
          onSaved={() => { getProposalsForOpportunity(opp.id).then(setProposals).catch(() => {}); if (onChanged) onChanged() }} />
      )}
    </>
  )
}

// The Opportunity-side counterpart to CrmLeadDetailModal's NextStepCard — same URGENCY color
// map, same shape, deliberately kept simpler (Opportunities only ever surface a 'log' action or
// none — countersigning/converting happen from the Proposals section below, not this card).
function OpportunityNextStepCard({ step, onLog }) {
  const u = URGENCY[step.urgency] || URGENCY.amber
  return (
    <div style={{ background: u.bg, border: `0.5px solid ${u.border}`, borderRadius: 8, padding: '12px 14px', margin: '2px 0 14px' }}>
      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: u.accent, marginBottom: 4 }}>{u.label}</div>
      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 3 }}>{step.title}</div>
      <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', lineHeight: 1.5 }}>{step.desc}</div>
      {step.action === 'log' && (
        <button style={{ ...F.btnGhost, marginTop: 10, padding: '5px 12px', fontSize: 12 }} onClick={onLog}>Log interaction</button>
      )}
    </div>
  )
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div style={F.label}>{label}</div>
      <div style={{ fontFamily: mono ? '"DM Mono", monospace' : '"DM Sans", sans-serif', fontSize: 13, color: value ? 'var(--aj-text)' : 'var(--aj-text-mid)' }}>{value || '—'}</div>
    </div>
  )
}

// Always-editable text input — keeps its own typing state so keystrokes don't PATCH on every
// change, and commits (onCommit) only on blur/Enter when the value actually changed. Re-syncs
// from `value` if the parent reverts it (a failed save).
function TextField({ label, value, onCommit, placeholder }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => { setDraft(value) }, [value])
  return (
    <div>
      <div style={F.label}>{label}</div>
      <input
        style={F.input}
        value={draft}
        placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { if (draft !== value) onCommit(draft) }}
        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur() }}
      />
    </div>
  )
}

function SelectField({ label, value, onCommit, options }) {
  return (
    <div>
      <div style={F.label}>{label}</div>
      <select style={F.input} value={value} onChange={e => onCommit(e.target.value)}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
}

const S = {
  metaRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  systemStageBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 20, padding: '5px 12px', cursor: 'default' },
  prob: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text-mid)' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 },
  addrGrid: { display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 12, marginBottom: 8 },
  subRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' },
  subEmail: { flex: 1, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)' },
  subEmailInactive: { color: 'var(--aj-text-mid)', textDecoration: 'line-through' },
  subDate: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)' },
  subBadge: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '2px 7px' },
  placeholder: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', fontStyle: 'italic', background: 'var(--aj-bg-subtle)', borderRadius: 6, padding: '10px 12px', margin: '8px 0' },
  journeyBox: { background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 8, padding: '11px 13px', margin: '8px 0' },
  journeyLabel: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  journeyHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', marginTop: 8, lineHeight: 1.5 },
  sectionRow: { display: 'flex', alignItems: 'center', margin: '14px 0 6px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  sectionLbl: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  propRow: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', padding: '8px 0', cursor: 'pointer', textAlign: 'left' },
  propType: { fontFamily: '"DM Mono", monospace', fontSize: 10, fontWeight: 600, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  propStatus: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', flexShrink: 0 },
  propFee: { fontFamily: '"DM Mono", monospace', fontSize: 12, color: 'var(--aj-text)', width: 96, textAlign: 'right', flexShrink: 0 },
  histHeadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '16px 0 6px', borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  histHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  histRow: { display: 'flex', gap: 11, padding: '9px 0', borderBottom: '0.5px solid var(--aj-border)' },
  histIcon: { fontSize: 14, width: 20, flexShrink: 0, textAlign: 'center' },
  histType: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)' },
  histNotes: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 2, lineHeight: 1.4 },
  histMeta: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)', marginTop: 3 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '4px 0' },
}
